<script setup lang="ts">

const props = defineProps<{
  route?: {
    name: string
    title: string
    slug?: string
    path?: string[]
  }
  parent?: {
    name: string
    params?: Record<string, string>
  }
  includeBreadcrumbs?: boolean,
  pageNav?: PageNavigationItem[],
  controlsOnly?: boolean
}>()

const globalPageGroups = useState<any[]>('globalPageGroups')
const globalPages = useState<any[]>('globalPages')
const sharedData = useState<any[]>('sharedData')
const menuData = useState<any>('menuData')

const routeToGlobalType: Record<string, string> = {
  'news': 'news-page',
  'news-slug': 'news-page',
  'people': 'people-page',
  'people-slug': 'people-page',
  'programme': 'programme-page',
  'programme-type': 'programme-page',
  'programme-type-slug': 'programme-page',
  'rooms': 'rooms-page',
  'rooms-slug': 'rooms-page',
  'apply': 'apply-page',
  'newsletter': 'newsletter-page',
}

const currentRoute = useRoute()
const routePath = computed(() => {
  const p = currentRoute.params.path
  return Array.isArray(p) ? p : p ? [p] : []
})

const globalPageGroup = computed(() => {
  if (!props.includeBreadcrumbs) return null
  const group = globalPageGroups.value?.find((g: any) => g.main?.slug === props.route?.slug)
  if (!group) return null

  const breadcrumbs: any[] = [{ ...group.main, path: null }]

  if (routePath.value.length >= 1) {
    const level = group.levels?.find((l: any) => l.main?.slug === routePath.value[0])
    if (level) {
      breadcrumbs.push({ ...level.main, path: [level.main.slug] })
      if (routePath.value.length >= 2) {
        const child = level.children?.find((c: any) => c.slug === routePath.value[1])
        if (child) breadcrumbs.push({ ...child, path: [level.main.slug, child.slug] })
      }
    }
  }

  return breadcrumbs
})

const parentTitle = computed(() => {
  if (!props.parent) return null
  if (props.parent.title) return props.parent.title
  const globalType = routeToGlobalType[props.parent.name]
  return globalPages.value?.find((p: any) => p.globalType === globalType)?.title ?? null
})

const color = computed(() => {
  const menuItems = menuData.value?.groups?.flatMap((group: any) => group?.links) || []

  const nuxtRouteName = (currentRoute.name as string)?.split('___')[0] || ''

  return menuItems.find((item: any) => {
    const link = item?.link
    if (link?.linkType !== 'internalLink') return false
    const { relationTo, value } = link.internalLink ?? {}

    if (relationTo === 'global_pages') {
      const target = routeToGlobalType[nuxtRouteName]
      return target && value?.globalType === target
    }

    if (relationTo === 'pages' && props.route?.slug) {
      if (value?.slug === props.route.slug) return true

      const doc = value?.group?.docs?.[0]
      const groupId = doc instanceof Object ? (doc as any).id : doc
      if (groupId) {
        const pageGroup = globalPageGroups.value?.find((g: any) => g?.id === groupId)
        return pageGroup?.main?.slug === props.route.slug
      }
    }

    return false
  })?.color
})

// The accent is an arbitrary colour picked per menu item, so anything painted
// on top of it needs a contrast colour derived from it. Emitting that here (as
// opposed to in the components that sit on accent backgrounds) keeps it correct
// server-rendered, before any client JS can read the computed accent back.
useHead({
  htmlAttrs: {
    style: computed(() => color.value
      ? `--color--accent: ${color.value}; --color--accent-contrast: ${contrastTextColor(color.value, { fallback: 'var(--color--black)' })}`
      : '')
  }
})

const showMenu = useState('showMenu')
const showSearch = useState('showSearch')

function scrollToPageNavItem(id: string) {
  scrollToHash(id)
}
</script>

<template>
  <header class="mx-1 mb-3 relative z-1 pointer-events-none ">
    <div
      class="text-md"
      :class="props.controlsOnly ? 'flex justify-end' : 'grid grid-cols-[1fr_auto] gap-0_5 items-start <sm:block'"
    >
      <div
        v-if="!props.controlsOnly"
        class="pointer-events-auto pt-1 min-w-0"
      >
        <!-- below sm the breadcrumbs run the full width and only the first line
             steps around the icons, so the flex row becomes inline flow there
             and this float reserves the icon corner. The gap-x is reproduced as
             a margin because gap does not apply to inline flow. -->
        <div
          class="flex flex-wrap gap-x-1 <sm:block <sm:[&_a]:inline-block <sm:[&_a]:mr-1 <sm:[&_span]:inline-block <sm:[&_span]:mr-1"
        >
          <div
            aria-hidden="true"
            class="hidden <sm:block float-right h-[var(--lh)] w-[calc(var(--icon-search--size)_+_var(--unit--vertical)_+_var(--icon-menu--width)_+_var(--unit--horizontal)_*_0.5)]"
          />

          <site-link
            :route-object="{
              name: 'index'
            }"
            class="hover:text-accent text-nowrap"
          >
            {{ sharedData?.siteName || $t('site.title') }}
          </site-link>

          <div
            v-if="(props.route || parent) && !globalPageGroup"
            :style="{ color: color || 'var(--color--gray)' }"
            class="flex flex-wrap gap-x-1 <sm:inline"
          >
            <site-link
              v-if="parent"
              :route-object="{ name: parent.name, params: parent.params }"
              class="[&:not([data-current])]:hover:text-black"
            >
              {{ parentTitle }}
            </site-link>

            <site-link
              v-if="props.route?.name"
              :route-object="{
                name: props.route.name,
                params: props.route.slug ? { slug: props.route.slug } : undefined
              }"
              class="[&:not([data-current])]:hover:text-black"
            >
              {{ props.route.title }}
            </site-link>
            <span v-else-if="props.route?.title">{{ props.route.title }}</span>
          </div>

          <div
            v-if="globalPageGroup"
            :style="{ color: color || 'var(--color--gray)' }"
            class="flex flex-wrap gap-x-1 <sm:inline"
          >
            <site-link
              v-for="(link, index) in globalPageGroup"
              :key="link.slug"
              :route-object="{
                name: index === 0 ? 'slug' : 'slug-path',
                params: {
                  slug: globalPageGroup[0].slug,
                  path: link.path
                }
              }"
              class="[&:not([data-current])]:hover:text-black"
            >
              {{ link?.title }}
            </site-link>
          </div>
        </div>

        <nav
          v-if="props?.pageNav?.length"
          class="!text-md pointer-events-auto container-d mt-1 text-accent <sm:hidden"
        >
          <div
            v-for="(item, index) in props.pageNav"
            :key="item.id"
            class="inline-block"
          >
            <button
              type="button"
              class="text-hover-accent"
              @click="scrollToPageNavItem(item.id)"
            >
              {{ truncateText(item.title, 3) }}
            </button>{{ index < props.pageNav.length - 1 ? ',&nbsp;' : '' }}
          </div>
        </nav>
      </div>

      <!-- the buttons are pinned to the viewport, so the wrapper keeps their grid
           track (and its width: search icon + gap + menu) reserved while the
           breadcrumbs scroll away underneath. Below sm the grid is gone and the
           float inside the breadcrumbs reserves that corner instead, so the
           track collapses to nothing — zero-sized rather than hidden, because
           `display: none` would take the pinned buttons down with it. -->
      <div
        class="mt-1 h-[var(--lh)] w-[calc(var(--icon-search--size)_+_var(--unit--vertical)_+_var(--icon-menu--width))]"
        :class="!props.controlsOnly && '<sm:mt-0 <sm:h-0 <sm:w-0'"
      >
        <div
          data-scroll-clearance
          class="fixed top-0 right-0 z-1 mt-1 mr-1 flex items-center gap-1 pointer-events-auto h-[var(--lh)]"
        >
          <button
            @click="showSearch = !showSearch"
            :aria-label="$t('search.label')"
            class="group"
          >
            <svgo-search class="icon-search overflow-visible stroke-black stroke-default group-hover:stroke-accent icon-hover" />
          </button>

          <button
            @click="showMenu = true"
            :aria-label="$t('menu.label')"
            class="group icon-box-menu"
          >
            <svgo-menu
              class="w-full h-full overflow-visible stroke-black stroke-default group-hover:stroke-accent icon-hover"
            />
          </button>
        </div>
      </div>
    </div>
  </header>
</template>
